/**
 * Deep Zoom arithmetic, shared by the tile source and the batcher.
 *
 * TWIN: `backend/src/kalqa/domains/files/renditions.py` — `PageGeometry.tile_grid` and
 * `single_tile_levels` are the same two functions, and `MAX_TILES_PER_REQUEST` is the same
 * number. Duplicated deliberately (ADR-0010's forced-conversation pattern): the server cannot
 * hand the client a function, and the client cannot ask the server what a viewport needs. If
 * one side changes, the other must — and `tests/.../test_rendition_geometry.py` executes the
 * A0 derivation on the Python side, so a divergence shows up as two files disagreeing about
 * numbers that are written down in `docs/notes/2026-08-20-viewer-canvas.md` §3.
 */

/**
 * The server's ceiling on one signing request, from `renditions.MAX_TILES_PER_REQUEST`.
 *
 * Crossing it is a `409`, not a truncation, so the batcher splits rather than hoping.
 */
export const MAX_TILES_PER_REQUEST = 256;

export interface PageSize {
  readonly width: number;
  readonly height: number;
  /** A **count**. Deep Zoom's deepest level index is `levels - 1`; level 0 is the 1x1 image. */
  readonly levels: number;
}

export function maxLevel(page: PageSize): number {
  return Math.max(page.levels - 1, 0);
}

/** `[columns, rows]` at that level. Overlap does not change the count. */
export function tileGrid(page: PageSize, level: number, tileSize: number): readonly [number, number] {
  const shrink = 2 ** Math.max(maxLevel(page) - level, 0);
  const width = Math.max(Math.ceil(page.width / shrink), 1);
  const height = Math.max(Math.ceil(page.height / shrink), 1);
  return [Math.max(Math.ceil(width / tileSize), 1), Math.max(Math.ceil(height / tileSize), 1)];
}

/**
 * The levels small enough to be a single tile — the zoomed-out ladder.
 *
 * The client computes this rather than counting what the server sent, because the server only
 * bootstraps **page 0**. Turning to page 3 needs the same ladder and the same one-tile-per-
 * level requests, and a viewer that only knew about the tiles it was handed would have a fast
 * first page and a slow every-other-page.
 */
export function singleTileLevels(page: PageSize, tileSize: number): number[] {
  const ladder: number[] = [];
  for (let level = 0; level < page.levels; level += 1) {
    const [columns, rows] = tileGrid(page, level, tileSize);
    if (columns > 1 || rows > 1) break;
    ladder.push(level);
  }
  return ladder;
}

/**
 * The deepest single-tile level — the low-res placeholder the viewer paints first.
 *
 * `null` when the page has no such level at all, which cannot happen for a real sheet (level 0
 * is 1x1 pixel) but is what a malformed geometry row would produce.
 */
export function placeholderLevel(page: PageSize, tileSize: number): number | null {
  const ladder = singleTileLevels(page, tileSize);
  return ladder.length === 0 ? null : ladder[ladder.length - 1] ?? null;
}
